import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Users, Mail, Globe, Smartphone, MessageSquare, Phone, Video } from 'lucide-react';

const CommunicationCircle = ({ item, size }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Define the pulse animation keyframes dynamically
  const pulseKeyframes = `
    @keyframes pulse-animation-${item.id} {
      0% {
        transform: scale(1);
        opacity: 0.7;
      }
      100% {
        transform: scale(2.5);
        opacity: 0;
      }
    }
  `;

  const IconComponent = {
    'face-to-face': Users,
    'email-phone': Mail,
    'website': Globe,
    'app': Smartphone,
    'business-messaging': MessageSquare,
    'calling': Phone,
    'video': Video,
  }[item.id];

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: `rgb(${item.color[0]}, ${item.color[1]}, ${item.color[2]})`,
        flexShrink: 0,
        boxShadow: `0 0 0 6px rgb(${item.color[0]}, ${item.color[1]}, ${item.color[2]}, 0.6)`,
        cursor: 'pointer',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Inject keyframes into the DOM */}
      <style>{pulseKeyframes}</style>

      {/* Inner circle for the image/icon (white background) */}
      <Box
        sx={{
          position: 'absolute',
          width: 'calc(100% - 12px)',
          height: 'calc(100% - 12px)',
          borderRadius: '50%',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
        }}
      >
        {item.image ? (
          <img
            src={item.image}
            alt={`${item.label} icon`}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
            onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/${size}x${size}/cccccc/333333?text=Icon`; }}
          />
        ) : IconComponent ? (
          <IconComponent size={size * 0.5} color={`rgb(${item.color[0]}, ${item.color[1]}, ${item.color[2]})`} />
        ) : (
          <Typography variant="caption" sx={{ color: 'gray.500', fontSize: '0.75rem' }}>No Icon</Typography>
       )}
      </Box>

      {/* Pulse layers (3 of them) */}
      {[0, 1, 2].map((i) => (
        <Box
          key={`pulse-${item.id}-${i}`}
          className="pulse-layer"
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: `rgba(${item.color[0]}, ${item.color[1]}, ${item.color[2]}, 0.7)`,
            zIndex: 1,
            animation: isHovered ? `pulse-animation-${item.id} 1.5s infinite ease-out` : 'none',
            animationDelay: `${i * 0.5}s`,
            transformOrigin: 'center center',
            opacity: isHovered ? 0.7 : 0,
            transition: 'opacity 0.3s ease-out', // Smooth opacity transition
          }}
        />
      ))}

      {/* Label below the circle */}
      <Typography
        variant="body2"
        sx={{
          // border: "1px solid red",
          position: 'absolute',
          bottom: '-40px',
          fontSize: '0.880rem',
          fontWeight: 650,
          color: 'text.secondary',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          width: '100%',
        }}
      >
        {item.label}
      </Typography>
    </Box>
  );
};

export default CommunicationCircle;